import { Pipe, PipeTransform } from '@angular/core';
import { Evse } from '@kempoweroyj/ngx-evse-api-client';
import { Transaction } from '@kempoweroyj/ngx-transactions-api-client';
import { HasCapabilityCategoryPipe } from 'shared/stations-service/station-capabilities.pipe';

@Pipe({ name: 'isCharging' })
export class IsChargingPipe implements PipeTransform {
    transform = isCharging;
}
export function isCharging(connector: Evse): boolean {
    if (connector?.status?.status === 'Occupied') {
        return isChargingTx(connector.tx);
    }

    return false;
}
export function isChargingTx(tx: Pick<Transaction, 'status' | 'updateTime'>): boolean {
    if (tx?.status === 'Charging') {
        const lastUpdatedSinceMs = Date.now() - Date.parse(tx.updateTime);
        const ACTIVE_THRESHOLD_MS = 15 * 60 * 1000; // if we haven't received an update in 15 minutes, it's probably no longer charging
        return lastUpdatedSinceMs < ACTIVE_THRESHOLD_MS;
    }

    return false;
}

@Pipe({ name: 'isOccupied' })
export class IsOccupiedPipe implements PipeTransform {
    constructor(private hasCapabilityCategoryPipe: HasCapabilityCategoryPipe) {}
    transform(connector: Evse, checkCharging = false): boolean {
        return isOccupied(
            connector,
            checkCharging,
            !this.hasCapabilityCategoryPipe.transform(connector?.stationId, 'KEMPOWER_NATIVE')
        );
    }
}
export function isOccupied(connector: Evse, checkCharging = false, nonKempowerNative?: boolean): boolean {
    if (checkCharging && isCharging(connector)) {
        return false;
    }
    if (connector?.status?.status === 'Occupied' && connector?.tx) {
        // Non kempowers don't necessarily send updates for tx after charging has ended
        if (nonKempowerNative) {
            return true;
        }
        const lastUpdatedSinceMs = Date.now() - Date.parse(connector.tx.updateTime);
        const ACTIVE_THRESHOLD_MS = 15 * 60 * 1000; // if we haven't received an update in 15 minutes, it's probably no longer charging
        return lastUpdatedSinceMs < ACTIVE_THRESHOLD_MS;
    }

    return false;
}

export function isConnectedNotCharging(tx: Pick<Transaction, 'status' | 'updateTime'>): boolean {
    if (isChargingTx(tx)) {
        return false;
    }
    if (tx?.status === 'PluggedInIdle' || tx?.status === 'InsulationTest') {
        const lastUpdatedSinceMs = Date.now() - Date.parse(tx.updateTime);
        const ACTIVE_THRESHOLD_MS = 15 * 60 * 1000; // if we haven't received an update in 15 minutes, it's probably no longer charging
        return lastUpdatedSinceMs < ACTIVE_THRESHOLD_MS;
    }

    return false;
}

@Pipe({ name: 'isAvailable' })
export class IsAvailablePipe implements PipeTransform {
    transform = isAvailable;
}
export function isAvailable(connector: Evse): boolean {
    return connector?.status?.status === 'Available';
}

@Pipe({ name: 'isUnknown' })
export class IsUnknownPipe implements PipeTransform {
    transform = isUnknown;
}
export function isUnknown(connector: Evse): boolean {
    return !connector?.status?.status || connector?.status?.status === 'Unknown';
}

@Pipe({ name: 'isUnavailable' })
export class IsUnavailablePipe implements PipeTransform {
    transform = isUnavailable;
}
export function isUnavailable(connector: Evse): boolean {
    return connector?.status?.status === 'Unavailable' && !isSelfTest(connector);
}

@Pipe({ name: 'isFaulted' })
export class IsFaultedPipe implements PipeTransform {
    transform = isFaulted;
}
export function isFaulted(connector: Evse): boolean {
    return connector?.status?.status === 'Faulted' && !isSelfTest(connector);
}

@Pipe({ name: 'isPreparing' })
export class IsPreparingPipe implements PipeTransform {
    constructor(private hasCapabilityCategoryPipe: HasCapabilityCategoryPipe) {}

    transform(connector: Evse): boolean {
        return isPreparing(
            connector,
            !this.hasCapabilityCategoryPipe.transform(connector?.stationId, 'KEMPOWER_NATIVE')
        );
    }
}
export function isPreparing(connector: Evse, nonKempowerNative?: boolean): boolean {
    if (nonKempowerNative) {
        return (
            isOccupied(connector, nonKempowerNative) &&
            !isCharging(connector) &&
            connector?.status?.detail === 'Preparing'
        );
    }
    return isOccupied(connector) && !isCharging(connector) && !connector?.tx?.chargingEndTime;
}

@Pipe({ name: 'isFinishing' })
export class IsFinishingPipe implements PipeTransform {
    constructor(private hasCapabilityCategoryPipe: HasCapabilityCategoryPipe) {}

    transform(connector: Evse): boolean {
        return isFinishing(
            connector,
            !this.hasCapabilityCategoryPipe.transform(connector?.stationId, 'KEMPOWER_NATIVE')
        );
    }
}
export function isFinishing(connector: Evse, nonKempowerNative?: boolean): boolean {
    if (nonKempowerNative) {
        return (
            isOccupied(connector, nonKempowerNative) &&
            !isCharging(connector) &&
            connector?.status?.detail !== 'Preparing'
        );
    }
    return isOccupied(connector) && !isCharging(connector) && !!connector?.tx?.chargingEndTime;
}

@Pipe({ name: 'isSelfTest' })
export class IsSelfTestPipe implements PipeTransform {
    transform = isSelfTest;
}
export function isSelfTest(connector: Evse): boolean {
    return connector?.status?.reason === 'POWER_ON_SELF_TEST';
}
